import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react';
import { UserDTO } from '../../application/dto/UserDTO';
import { LoginUseCase } from '../../application/use-cases/LoginUseCase';
import { LogoutUseCase } from '../../application/use-cases/LogoutUseCase';
import { GetCurrentUserUseCase } from '../../application/use-cases/GetCurrentUserUseCase';
import { RegisterUseCase } from '../../application/use-cases/RegisterUseCase';
import { SupabaseAuthRepository } from '../../infrastructure/repositories/SupabaseAuthRepository';
import { MockAuthRepository } from '../../infrastructure/repositories/MockAuthRepository';
import { UserRole } from '../../domain/value-objects/UserRole';
import { UserMapper } from '../../application/mappers/UserMapper';
import { supabase } from '@shared/config/supabase';
import { SupabaseEmailRepository, SendWelcomeEmailUseCase } from '@features/email-management';
import { toast } from 'sonner';

interface AuthContextValue {
  user: UserDTO | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, fullName: string, role: UserRole, workspaceId: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  useMock?: boolean; // Allow switching between Supabase and Mock for development
}

export function AuthProvider({ children, useMock = false }: AuthProviderProps) {
  const [user, setUser] = useState<UserDTO | null>(null);
  const userRef = useRef<UserDTO | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start with true to check session

  // Keep ref in sync with state so the auth listener always has the latest value
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Initialize repository and use cases - memoized to prevent recreation on every render
  // Use SupabaseAuthRepository by default, MockAuthRepository if useMock is true
  const authRepository = useMemo(
    () => (useMock ? new MockAuthRepository() : new SupabaseAuthRepository()),
    [useMock]
  );

  const useCases = useMemo(
    () => ({
      login: new LoginUseCase(authRepository),
      register: new RegisterUseCase(authRepository),
      logout: new LogoutUseCase(authRepository),
      getCurrentUser: new GetCurrentUserUseCase(authRepository),
    }),
    [authRepository]
  );

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    try {
      // Just trigger the login - the auth listener will set the user
      await authRepository.login(email, password);
      // Don't set user here - the listener will do it
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      throw error;
    }
    // Don't set isLoading to false here - the listener will do it when user is set
  }, [authRepository]);

  const loginWithGoogle = useCallback(async () => {
    setIsLoading(true);
    try {
      // Initiate Google OAuth - will redirect to Google
      await authRepository.loginWithGoogle();
      // After redirect back, the auth listener will handle the rest
    } catch (error) {
      console.error('Google login error:', error);
      setIsLoading(false);
      throw error;
    }
  }, [authRepository]);

  const register = useCallback(async (email: string, password: string, fullName: string, role: UserRole, workspaceId: string) => {
    setIsLoading(true);
    try {
      const response = await useCases.register.execute({ email, password, fullName, role, workspaceId });
      setUser(response.user);

      // Send welcome email (non-blocking - don't wait for result)
      if (response.user && workspaceId && role !== 'admin') {
        const emailRepository = new SupabaseEmailRepository();
        const sendWelcomeEmail = new SendWelcomeEmailUseCase(emailRepository);
        sendWelcomeEmail.execute({
          userId: response.user.id,
          userEmail: email,
          userName: fullName,
          userRole: role,
          workspaceId: parseInt(workspaceId, 10),
        }).catch((err) => {
          // Log error but don't fail registration
          console.warn('Failed to send welcome email:', err);
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [useCases.register]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await useCases.logout.execute();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [useCases.logout]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await useCases.getCurrentUser.execute();
      if (response.user) {
        setUser(response.user);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  }, [useCases.getCurrentUser]);

  // Check for existing session on mount
  useEffect(() => {
    if (useMock) {
      // Skip session check for mock mode
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    let initialCheckDone = false; // Tracks whether checkSession has completed
    let fetchingUser = false; // Prevent concurrent fetches

    const fetchUserProfile = async (userId: string, session: { user: { app_metadata?: Record<string, unknown>; user_metadata?: Record<string, unknown>; email?: string; id: string } }): Promise<void> => {
      fetchingUser = true;
      try {
        let userProfile = await authRepository.findById(userId);

        // If no profile exists, check if this is an OAuth user and create profile
        if (!userProfile && session.user.app_metadata?.provider === 'google') {
          console.log('Creating profile for OAuth user...');
          const googleId = (session.user.user_metadata?.sub as string) || session.user.id;
          const email = session.user.email || '';
          const fullName = (session.user.user_metadata?.full_name as string) ||
                          (session.user.user_metadata?.name as string) ||
                          email.split('@')[0];
          const avatar = (session.user.user_metadata?.avatar_url as string) ||
                        (session.user.user_metadata?.picture as string);

          const supabaseRepo = authRepository as SupabaseAuthRepository;
          userProfile = await supabaseRepo.createOrUpdateOAuthUser(
            session.user.id,
            email,
            fullName,
            googleId,
            avatar
          );
        }

        if (userProfile && isMounted) {
          const userDTO = UserMapper.toDTO(userProfile);
          setUser(userDTO);
        } else if (!userProfile && isMounted) {
          // Profile doesn't exist (deleted user/workspace) - sign out
          console.warn('User profile not found after sign in. Account may have been deleted.');
          await supabase.auth.signOut();
          setUser(null);
          toast.error('Tu cuenta ha sido eliminada', {
            description: 'Contacta soporte si crees que es un error.',
            duration: 8000,
          });
        }
      } finally {
        fetchingUser = false;
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const checkSession = async () => {
      try {
        console.log('Checking session...');
        const response = await useCases.getCurrentUser.execute();
        if (isMounted) {
          if (response.user) {
            console.log('User found from session:', response.user);
            setUser(response.user);
          } else {
            console.log('No user found in session');
          }
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        initialCheckDone = true;
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);

      if (event === 'SIGNED_IN' && session && !fetchingUser) {
        // Skip if user is already loaded (tab focus, token refresh, concurrent events)
        if (userRef.current?.id === session.user.id) {
          console.log('User already loaded, skipping re-fetch');
          setIsLoading(false);
          return;
        }

        // Skip if checkSession is still handling the initial load
        if (!initialCheckDone) {
          console.log('Initial session check in progress, skipping listener fetch');
          return;
        }

        // Actual new sign-in (different user or first time)
        try {
          await fetchUserProfile(session.user.id, session);
        } catch (error) {
          console.error('Failed to fetch user after sign in:', error);
          if (isMounted) {
            toast.error('Error de conexión', {
              description: 'No se pudo verificar tu perfil. Intenta recargar la página.',
              duration: 8000,
            });
            setIsLoading(false);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        if (isMounted) {
          setUser(null);
        }
      } else if (event === 'TOKEN_REFRESHED' && session) {
        console.log('Token refreshed, user already loaded');
      }
    });

    // Cleanup subscription
    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [useMock, useCases.getCurrentUser, authRepository]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      login,
      loginWithGoogle,
      register,
      logout,
      refreshUser,
      isLoading,
    }),
    [user, login, loginWithGoogle, register, logout, refreshUser, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
